// documents.doc — pembangun HTML dokumen (Nota Servis, Tanda Terima). Self-contained utk cetak.
// Ke pelanggan tampil TOTAL saja (modal internal). Port perilaku notaServisHTML/tandaTerimaHTML prototype.
import { rp, esc, maskPhone } from '@domain/format';
import { billedNet, isPiutangCara } from '@domain/money';
import { qrSVG } from '@domain/qr';
import { skTanda, skKhusus, skKuasa, skTracking, profil, logoToko, getSetting } from '@core/settings';
import { terapkanTata, gayaBlokCss, teksBlok, semuaBlok, isiTambahan, PREFIKS_TAMBAHAN,
  type SecDef, type TataSimpan, type GayaBlok } from '@domain/doclayout';
import type { NotaData, TandaData, DocMeta } from './documents.repo';

/** Logo di kop dokumen. Tinggi dalam piksel; lebar mengikuti sendiri agar tak gepeng saat dicetak. */
// Tinggi dijaga setara baris wordmark lama (±26 px ≈ 6,9 mm): nota sudah memakai hampir
// seluruh tinggi ½ A4, jadi menambah beberapa milimeter di kop bisa melemparkannya ke lembar kedua.
const logoKop = (tinggiPx: number): string =>
  // max-width wajib: logo berbentuk banner (aspek >20:1) bisa melebar melewati bingkai dokumen
  // dan menjepit blok kontak cabang di sebelahnya.
  `<img src="${esc(logoToko())}" alt="iFiCs" style="height:${tinggiPx}px;width:auto;max-width:220px;object-fit:contain;display:block">`;

/**
 * Alamat aplikasi yang DICETAK ke kertas pelanggan.
 *
 * Bukan sekadar `location.origin`: kalau kasir kebetulan membuka aplikasi lewat alamat jaringan
 * lokal (mis. 192.168.1.5:5173), nota akan membawa alamat itu — mati begitu pelanggan pulang.
 * Karena itu Apo bisa mengunci alamat resminya di Pengaturan → Profil Toko; kosong = ikut alamat
 * yang sedang dipakai (benar untuk pemakaian sehari-hari setelah dideploy).
 */
export const alamatPublik = (): string => {
  const set = (getSetting<{ alamatPublik?: string }>('profil', {}).alamatPublik ?? '').trim();
  const dasar = set || globalThis.location?.origin || 'https://ifics.app';
  return dasar.replace(/\/+$/, '');
};

// Halaman publik lacak servis. Dipasang di alamat yang sama dengan aplikasi, jadi ia ikut
// pindah sendiri saat aplikasi dideploy ke domain asli — tak ada alamat contoh yang tertinggal
// di kertas pelanggan. `globalThis.location` dijaga: dokumen juga dirender di uji (tanpa DOM).
export const trackBase = (): string => `${alamatPublik()}/lacak.html#t=`;
// Sama seperti TRACK_BASE: ikut alamat aplikasi supaya berpindah sendiri saat dideploy — tak ada
// alamat contoh yang tertinggal di kertas pelanggan.
export const garansiBase = (): string => `${alamatPublik()}/garansi.html#t=`;

// Pernyataan hukum Surat Kuasa (editable via Editor Dokumen — menyusul). {pengambil} = nama.
/** Teks isian Apo: aman dari kode, tapi <b>/<i>/<u>/<br> tetap berfungsi untuk penegasan. */
const teksKaya = (t: string): string =>
  esc(t).replace(/&lt;(\/?(?:b|i|u|br\s*\/?))&gt;/g, '<$1>');

const DIWAK_PERNYATAAN = 'Dengan menandatangani surat ini, saya {pengambil} menyatakan dengan sebenarnya dan tanpa paksaan: (1) Saya telah <b>MENGAMBIL</b> unit servis dengan data di atas dalam keadaan sebagaimana saya terima. (2) Saya berhak / berwenang penuh mengambil unit tersebut dan unit BUKAN merupakan barang hasil kejahatan. (3) Seluruh identitas dan data yang saya berikan adalah benar dan terlampir. (4) Saya <b>MEMBEBASKAN iFiCs beserta Suplier Groups dan seluruh karyawannya</b> dari segala bentuk tuntutan, gugatan, klaim, ganti rugi, atau tanggung jawab hukum apa pun — baik perdata maupun pidana — yang timbul sekarang maupun di kemudian hari sehubungan dengan penyerahan unit ini, termasuk apabila terdapat pihak lain yang mengaku sebagai pemilik. (5) Segala risiko dan akibat hukum atas pengambilan ini menjadi tanggung jawab saya sepenuhnya.';

export interface Pengambil { nama: string; ktp: string; alamat: string; hp: string; hubungan: string }

// S&K Tanda Terima kini editable via Pengaturan (core/settings skTanda()).

const DOC_CSS = `*{font-family:Arial,sans-serif;box-sizing:border-box}body{margin:0;padding:14px;color:#111;font-size:12px}
  .doc{max-width:560px;margin:0 auto;border:1px solid #999;padding:14px}
  .h{display:flex;justify-content:space-between;border-bottom:2px solid #0A2A4A;padding-bottom:8px;margin-bottom:8px}
  .b{font-size:20px;font-weight:bold;color:#0A2A4A}.b span{color:#1FA9E0}.c{font-size:11px;text-align:right;color:#444}
  .no{background:#EAF4FB;border:1px solid #1FA9E0;border-radius:6px;padding:6px 10px;font-weight:bold;color:#0A2A4A;display:inline-block;margin-bottom:8px}
  table{width:100%;border-collapse:collapse;margin-bottom:8px}td{padding:3px 4px;vertical-align:top}.k{color:#555;width:96px}
  .box{border:1px solid #bbb;border-radius:5px;padding:8px;margin-bottom:8px}
  .ttl{font-weight:bold;color:#0A2A4A;border-bottom:1px solid #ddd;margin:8px 0 4px;padding-bottom:2px}
  .sk{font-size:10px;color:#444;padding-left:16px}.sk li{margin-bottom:2px}
  .ttd{display:flex;justify-content:space-between;gap:10px;margin-top:10px;text-align:center;font-size:11px}.ttd>div{width:45%}.sig{margin-top:32px;border-top:1px solid #333}
  svg{max-width:100%;height:auto;display:block}
  @media print{body{padding:0}}`;


/**
 * Blok "Ikuti Kami" — SATU QR untuk semua akun, bukan satu QR per akun.
 * Alasannya kertas: nota & tanda terima sudah hampir memenuhi ½ A4; tiga barcode berikut
 * keterangannya menghabiskan ±25 mm dan melemparkan dokumen ke lembar kedua. QR mengarah ke
 * satu tautan kumpulan (Linktree dsb) yang isinya bisa Apo ubah kapan saja tanpa cetak ulang.
 *
 * Belum ada akun yang diisi → mengembalikan '' → bloknya hilang sama sekali (0 mm terpakai).
 */
export function sosmedBlok(opts?: { extra?: { label: string; url: string }[]; qr?: boolean; teks?: string }): string {
  const extra = opts?.extra;
  const pakaiQr = opts?.qr !== false;
  const p = profil();
  const bersih = (v: string | undefined): string => (v ?? '').trim();
  const tautan = (v: string, pola: string): string =>
    !v ? '' : (/^https?:\/\//i.test(v) ? v : pola + v.replace(/^@/, ''));

  const ig = bersih(p.instagram), tt = bersih(p.tiktok), fb = bersih(p.facebook), web = bersih(p.website);
  const qrUrl = bersih(p.sosmedHub) || tautan(ig, 'https://instagram.com/') || tautan(tt, 'https://tiktok.com/@') || web;

  const baris: string[] = [];
  if (ig) baris.push(`IG ${esc(ig)}`);
  if (tt) baris.push(`TikTok ${esc(tt)}`);
  if (fb) baris.push(`FB ${esc(fb)}`);
  if (bersih(p.wa)) baris.push(`WA ${esc(p.wa)}`);
  if (web) baris.push(esc(web));
  const lain = (extra ?? []).filter((x) => x.url).map((x) => `${esc(x.label)}: <b>${esc(x.url)}</b>`);

  if (!baris.length && !lain.length && !(pakaiQr && qrUrl)) return '';
  // QR px=2 (±19 mm tercetak): masih nyaman dipindai kamera HP, tapi separuh tinggi px=3.
  const qr = pakaiQr && qrUrl ? `<div style="flex:none">${qrSVG(qrUrl, 2)}</div>` : '';   // ±19 mm: cukup dipindai, hemat ruang
  // Tanpa QR (tanda terima) → SATU BARIS teks polos, bukan kotak. Terukur: sebagai kotak
  // ia menambah 9,6 mm dan tanda terima melewati batas 129 mm ½ A4 (126,7 → 136,3 = dua
  // lembar); sebagai baris hanya ±4 mm. Kertas menang atas hiasan.
  if (!pakaiQr) {
    const isi = [...baris, ...(qrUrl ? [esc(qrUrl)] : []), ...lain].join(' · ');
    return isi ? `<div style="text-align:center;font-size:9.5px;color:#555;margin-top:4px">📣 Ikuti kami · ${isi}</div>` : '';
  }
  // Ber-QR (nota) → kartu setinggi tetangganya dalam baris ⅓. Kalimatnya diatur Apo di
  // Pengaturan → 📄 Dokumen & Template; {akun} diisi daftar akun dari Profil Toko.
  const kalimat = (opts?.teks ?? '📣 <b>Ikuti kami</b><br>{akun}')
    .replaceAll('{akun}', baris.join(' · '));
  return `<div class="g" style="display:flex;gap:8px;align-items:center;flex-wrap:nowrap;background:#fff7ec;border-color:#b9770f;font-size:10.5px;line-height:1.4">${qr}
    <div>${teksKaya(kalimat)}${lain.length ? `<br>${lain.join(' · ')}` : ''}</div></div>`;
}

/**
 * BLOK Nota Servis. Susunan bawaan memakai kertas ½ A4 MELEBAR: kartu garansi dan QR survei
 * ditaruh berdampingan (½ + ½) — kalau ditumpuk, notanya lewat batas satu lembar.
 * Blok "garansi" hanya tercetak bila tiket memang bergaransi (permintaan Apo 21 Jul).
 */
export const NOTA_SECTIONS: SecDef[] = [
  { id: 'kop', label: 'Kop toko (logo, cabang, kontak)', wajib: true },
  { id: 'judul', label: 'Judul dokumen', teks: 'NOTA SERVIS' },
  { id: 'info', label: 'Info servis (no, tanggal, pelanggan, unit)', wajib: true },
  { id: 'biaya', label: 'Rincian biaya & status bayar', wajib: true },
  // WAJIB: nota adalah satu-satunya dokumen yang membawa masa garansi + QR verifikasi ke
  // pelanggan. Kalau blok ini bisa disembunyikan, pelanggan bergaransi kehilangan bukti klaim
  // tanpa ada yang sadar. Tetap otomatis hilang pada tiket TANPA garansi (blok kosong dibuang).
  // Tiga kartu ini BERBAGI SATU BARIS (⅓ masing-masing). Nota tiket nyata terukur 121 mm dari
  // 129 mm yang tersedia di ½ A4 — sisa 8 mm, sedangkan satu QR saja setinggi 19 mm. Jadi kartu
  // "ikuti kami" menumpang baris yang sudah ada, bukan menambah baris baru. Yang kosong
  // (mis. tiket tanpa garansi) hilang sendiri dan sisanya melebar.
  { id: 'garansi', label: 'Kartu Garansi + QR (hanya bila ada garansi)', width: 'third', wajib: true,
    teks: '🛡️ <b>Kartu Garansi</b> — berlaku <b>{garansi}</b> sejak pengambilan. Simpan nota ini sebagai bukti klaim. Scan QR untuk verifikasi garansi.' },
  { id: 'survei', label: 'QR Survei kepuasan', width: 'third',
    teks: '⭐ <b>Puas dengan layanan kami?</b> Scan QR untuk beri rating & ulasan singkat. Terima kasih!' },
  { id: 'sosmed', label: 'Ikuti kami (QR akun sosial media)', width: 'third',
    teks: '📣 <b>Ikuti kami</b><br>{akun}<br>scan QR untuk semua tautan' },
  { id: 'footer', label: 'Tagline penutup' },
];

/** Isi satu blok nota. Dipisah dari penyusunnya supaya urutannya bisa diatur Apo. */
function notaSec(id: string, d: NotaData, tks: (id: string) => string, tata: TataSimpan): string {
  const total = billedNet(d.ticket);
  const bs = d.ticket.biayaSimpan ?? 0;      // biaya simpan (pendapatan lain-lain, baris terpisah)
  const grand = total + bs;
  const dp = d.ticket.dp ?? 0;
  const sisa = Math.max(0, grand - dp);
  switch (id) {
    case 'kop': {
      const kontak = [d.outletAlamat, d.outletWa].filter(Boolean).map(esc).join(' · ');
      return `<div class="h">${logoKop(26)}<div class="sub">${esc(d.outletNama)}${kontak ? ' · ' + kontak : ''}</div></div>`;
    }
    case 'judul':
      return `<div class="title">${esc(tks('judul'))}</div>`;
    case 'info':
      return `<div class="row"><span class="k">No. Servis</span><b>${esc(d.no)}</b></div>
  <div class="row"><span class="k">Tanggal</span><span>${esc(d.tglAmbil)}</span></div>
  <div class="row"><span class="k">Pelanggan</span><span>${esc(d.custNama)}</span></div>
  <div class="row"><span class="k">Unit</span><span>${esc(d.unit)}</span></div>
  ${d.sn ? `<div class="row"><span class="k">S/N</span><span>${esc(d.sn)}</span></div>` : ''}
  <div class="row"><span class="k">Tindakan</span><span>${esc(d.tindakan)}</span></div>`;
    case 'biaya': {
      const bayarStatus = d.ticket.lunas ? 'LUNAS'
        // Piutang & Tempo sama-sama hutang → cetak jenisnya + jatuh tempo. Nota inilah pegangan
        // penagihan; kalau tanggalnya hilang dari dokumen, pelanggan & kasir kehilangan acuan.
        : (isPiutangCara(d.ticket.caraBayar) ? `${esc((d.ticket.caraBayar ?? 'PIUTANG').toUpperCase())}${d.jatuhTempo ? ' s/d ' + esc(d.jatuhTempo) : ''}` : 'BELUM LUNAS');
      return `<div class="tot">
    <div class="row"><span class="k">Biaya Servis</span><span>${rp(total)}</span></div>
    ${bs > 0 ? `<div class="row"><span class="k">Biaya Simpan</span><span>+ ${rp(bs)}</span></div>` : ''}
    <div class="row"><span class="k">Total Biaya</span><span>${rp(grand)}</span></div>
    ${dp > 0 ? `<div class="row"><span class="k">DP</span><span>${rp(dp)}</span></div><div class="row"><span class="k">Sisa</span><span>${rp(sisa)}</span></div>` : ''}
    <div class="row"><span>DIBAYAR (${esc(d.ticket.caraBayar ?? '-')})</span><span>${d.ticket.lunas ? rp(grand) : rp(dp)}</span></div>
    <div class="row"><span class="k">Status</span><b style="color:${d.ticket.lunas ? '#1d9e75' : '#c0392b'}">${esc(bayarStatus)}</b></div>
  </div>`;
    }
    case 'garansi': {
      // TANPA garansi → blok kosong; `secBody` membuangnya sekaligus melebarkan pasangannya.
      if (!d.garansi || d.garansi === 'Tidak Ada') return '';
      // Kotak TANPA ukuran tetap: QR memakai ukuran alaminya (±29 mm). Dipaksa 76px, tiap
      // modul cuma 0,54 mm — di batas gagal dipindai bila tinta printer melebar.
      // 19 mm (px 2), BUKAN 29 mm. Terukur pada tiket nyata: dengan QR 29 mm baris tiga-kartu
      // setinggi 34 mm dan nota menjadi 131 mm — lewat 129 mm yang tersedia di ½ A4, jadi dua
      // lembar. Dengan 19 mm nota 124,6 mm = tetap satu lembar. Modul ±0,53 mm: masih terbaca
      // kamera HP pada cetakan laser, tapi JANGAN dikecilkan lagi.
      const garQr = d.token ? `<div style="flex:none">${qrSVG(garansiBase() + d.token, 2)}</div>` : '';
      // `flex-wrap:nowrap`: aturan bawaan `.g{flex-wrap:wrap}` melempar teks ke BAWAH QR.
      // Apo minta bentuknya seperti kotak lacak di tanda terima — QR kiri, keterangan kanan.
      return `<div class="g" style="display:flex;gap:10px;align-items:center;flex-wrap:nowrap">${garQr}<div>${
        teksKaya(tks('garansi')).replaceAll('{garansi}', esc(d.garansi))}</div></div>`;
    }
    case 'survei':
      return survei(d.token, tks('survei'));
    case 'sosmed':
      return sosmedBlok({ teks: tks('sosmed') });
    case 'footer':
      return `<div class="ft">Terima kasih atas kepercayaan Anda.<br><b>${esc(profil().tagline)}</b></div>`;
    default:
      return blokTambahanHTML(id, tata);
  }
}

export function notaHTML(d: NotaData, opts?: { tata?: TataSimpan }): string {
  const tata = opts?.tata ?? docTata('nota');
  const secs = terapkanTata(semuaBlok(NOTA_SECTIONS, tata), tata);
  return `<!doctype html><html lang="id"><head><meta charset="utf-8"><title>Nota ${esc(d.no)}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:-apple-system,system-ui,sans-serif;color:#1d2733;margin:0;padding:16px;max-width:420px;margin:auto}
  .h{text-align:center;border-bottom:2px solid #0A2A4A;padding-bottom:8px;margin-bottom:10px}
  .brand{font-size:24px;font-weight:800;color:#0A2A4A;letter-spacing:.5px}.brand span{color:#1FA9E0}
  .sub{font-size:11px;color:#6b7a8a;margin-top:2px}
  .title{text-align:center;font-weight:700;margin:6px 0 10px;color:#0A2A4A}
  .row{display:flex;justify-content:space-between;padding:3px 0;font-size:13px;gap:12px}
  .row .k{color:#6b7a8a}
  .tot{border-top:1px dashed #999;margin-top:8px;padding-top:8px}
  .tot .row{font-weight:700;font-size:15px}
  /* Bantalan 6px & jarak atas 8px (dulu 8/12): sejak tulisan tak lagi boleh menembus kotak,
     ia membungkus ke bawah sehingga kartu meninggi dan nota pas-pasan 129,1 mm dari 129 mm
     yang tersedia. Dirapatkan → 127 mm. Jangan dilonggarkan lagi tanpa mengukur ulang. */
  .g{background:#e3f5ee;border:1px solid #1d9e75;border-radius:8px;padding:6px;margin-top:8px;font-size:12px;flex-wrap:wrap;height:calc(100% - 8px)}   /* −8px = margin-top; box-sizing tak mencakup margin → kotak luber keluar selnya */
  /* min-width:0 (bukan 150px): di kolom ⅓ selebar ±61 mm, minimum 150px memaksa kolom teks
     lebih lebar daripada selnya sehingga tulisan menembus garis kotak — persis yang terlihat
     pada cetakan Apo 22 Jul. overflow-wrap mematahkan kata/alamat panjang. */
  .g>div:last-child{flex:1;min-width:0;overflow-wrap:anywhere}
  svg{max-width:100%;height:auto;display:block}
  .gy,.gy *{font-size:inherit}
  .ft{text-align:center;color:#6b7a8a;font-size:11px;margin-top:14px;border-top:1px solid #dbe4ec;padding-top:8px}
  @media print{body{padding:0}}
</style></head>
<body>
  ${secBody(secs, (id) => notaSec(id, d, (x) => teksBlok(semuaBlok(NOTA_SECTIONS, tata).find((y) => y.id === x)!, tata), tata), tata.gaya)}
</body></html>`;
}

/** Blok QR Survei Kepuasan di Nota (toggle via Pengaturan → settings 'survei'). */
function survei(token: string, teks: string): string {
  const sv = getSetting<{ on?: boolean; base?: string }>('survei', {});
  if (sv.on === false || !token) return '';
  return `<div class="g" style="display:flex;gap:10px;align-items:center;flex-wrap:nowrap;background:#eef6ff;border-color:#1FA9E0">
    <div style="flex:none">${qrSVG((sv.base || `${alamatPublik()}/survei.html#t=`) + token, 2)}</div>
    <div>${teksKaya(teks)}</div></div>`;
}

/**
 * BLOK Tanda Terima Servis — urutan & sifat bawaan. Apo bisa mengurutkan ulang, menyembunyikan,
 * mengatur lebar/perataan lewat Pengaturan → 📄 Dokumen & Template. Simpanan hanya berisi
 * PERUBAHANNYA (lihat `@domain/doclayout`), jadi blok baru di sini tetap muncul sendiri.
 */
/** Pengaturan tata letak per dokumen, disimpan Apo (Pengaturan → 📄 Dokumen & Template). */
export const docTata = (doc: string): TataSimpan =>
  getSetting<Record<string, TataSimpan>>('doc_tata', {})[doc] ?? {};

/**
 * Susun blok jadi badan dokumen. Blok kosong (mis. cap GARANSI pada tanda terima biasa)
 * dibuang. Dua blok berlebar ½ yang BERURUTAN ditaruh berdampingan — itulah gunanya ½,
 * mis. Kartu Garansi di kiri dan QR Survei di kanan pada nota.
 */

/**
 * Nilai perataan yang boleh masuk ke atribut style. Datang dari tabel setelan, yang lewat UI
 * memang cuma dropdown — tapi setelan bisa juga ditulis lewat API oleh peran manajer, dan nilai
 * ini disisipkan mentah ke dalam `style="…"` pada dokumen yang lalu dirender di jendela cetak
 * satu asal (tanpa sandbox). Satu nilai karangan di situ = skrip berjalan di sesi orang yang
 * membuka pratinjau — biasanya Owner. Karena pilihannya cuma tiga, daftar-pilih adalah
 * penjagaan yang paling murah dan paling sulit salah.
 */
const rataAman = (v: unknown): '' | 'center' | 'right' =>
  (v === 'center' || v === 'right') ? v : '';

export function secBody(secs: SecDef[], render: (id: string) => string, gaya?: Record<string, GayaBlok>): string {
  // Blok kosong TIDAK disaring lebih dulu: kalau disaring, pasangan ½ bisa meleset. Contoh
  // nyata: cap GARANSI(½) + judul(½) — pada tanda terima biasa cap GARANSI kosong, dan judul
  // akan berpasangan dengan blok BERIKUTNYA. Satu susunan tersimpan jadi tampil beda-beda
  // antar tiket. Karena itu pasangan ditentukan dari urutan asli, baru isinya diperiksa.
  const bungkus = (id: string, html: string): string => {
    const css = gayaBlokCss(gaya?.[id]);
    if (!css) return html;
    // kelas `gy` hanya saat ukuran huruf disetel — isi blok punya ukurannya sendiri
    // (mis. `.sk{font-size:10px}`), jadi tanpa ini setelan Apo diabaikan diam-diam.
    const kls = gaya?.[id]?.fs != null ? ' class="gy"' : '';
    return `<div${kls} style="${css}">${html}</div>`;
  };
  const isi = secs.map((s) => { const h = render(s.id); return { s, html: h.trim() ? bungkus(s.id, h) : '' }; });
  const out: string[] = [];
  const ada = (x: { html: string }): boolean => !!x.html.trim();
  for (let i = 0; i < isi.length; i++) {
    const a = isi[i]!, b = isi[i + 1], c = isi[i + 2];
    const rata = (x: SecDef): string => (rataAman(x.align) ? ` style="text-align:${rataAman(x.align)}"` : '');
    if (a.s.width === 'third' && b?.s.width === 'third' && c?.s.width === 'third') {
      i += 2;
      // Yang kosong dibuang, sisanya berbagi rata — persis perilaku pasangan ½ di bawah.
      const isiAda = [a, b, c].filter(ada);
      if (!isiAda.length) continue;
      if (isiAda.length === 1) { out.push(isiAda[0]!.html); continue; }
      out.push(`<div style="display:flex;gap:8px;align-items:stretch">`
        + isiAda.map((x) => `<div style="flex:1;min-width:0"${rata(x.s)}>${x.html}</div>`).join('')
        + `</div>`);
    } else if (a.s.width === 'half' && b?.s.width === 'half') {
      i++;                                             // pasangannya sudah dipakai
      if (!ada(a) && !ada(b)) continue;
      if (!ada(a) || !ada(b)) { out.push(ada(a) ? a.html : b.html); continue; }   // sisanya melebar penuh
      out.push(`<div style="display:flex;gap:8px;align-items:stretch">`
        + `<div style="flex:1;min-width:0"${rata(a.s)}>${a.html}</div>`
        + `<div style="flex:1;min-width:0"${rata(b.s)}>${b.html}</div></div>`);
    } else if (!ada(a)) {
      continue;
    } else if (rataAman(a.s.align)) {
      out.push(`<div style="text-align:${rataAman(a.s.align)}">${a.html}</div>`);
    } else {
      out.push(a.html);
    }
  }
  return out.join('\n  ');
}

// `wajib` = tak bisa disembunyikan. Dipasang pada blok yang membuat dokumen ini SAH sebagai
// bukti: kop (identitas penerbit), S&K + S&K khusus (batas tanggung jawab: biaya simpan >3 bln,
// lepas tanggung jawab >6 bln), tanda tangan (bukti serah), nomor & data unit. Yang boleh
// disembunyikan hanya pelengkap: judul, cap GARANSI, QR lacak, tagline.
export const TANDA_SECTIONS: SecDef[] = [
  { id: 'kop', label: 'Kop toko (logo, cabang, kontak)', wajib: true, isiDari: 'Nama & kontak diambil dari Pengaturan → 🏪 Profil Toko dan data cabang.' },
  // Cap GARANSI & judul berdampingan: keduanya blok pendek, dan pada varian klaim garansi
  // menumpuknya membuat dokumen lewat 4,6 mm dari batas satu lembar ½ A4 (133,6 vs 129).
  // Pada tanda terima biasa cap kosong → judul otomatis melebar penuh.
  { id: 'garansiTag', label: 'Cap GARANSI (hanya untuk klaim garansi)', width: 'half', teks: 'GARANSI' },
  { id: 'judul', label: 'Judul dokumen', width: 'half', teks: 'TANDA TERIMA SERVIS' },
  { id: 'nomor', label: 'No. Servis', wajib: true },
  // Kertas ½ A4 MELEBAR: data unit di kiri, QR lacak di kanan. Kalau keduanya ditumpuk
  // (bawaan lama, warisan kertas tegak) tingginya 167 mm — lewat batas cetak 129 mm.
  { id: 'data', label: 'Data unit & keluhan', wajib: true, width: 'half' },
  { id: 'tracking', label: 'QR lacak status', width: 'half' },
  // S&K khusus sengaja SEBELUM tanda tangan: pelanggan menandatangani setelah membacanya.
  { id: 'skKhusus', label: 'S&K khusus (unit tak bisa diuji penuh)', wajib: true },
  // S&K dan tanda tangan LEBAR PENUH: blok tanda tangan sendiri sudah berisi dua kolom
  // (pelanggan + CS). Dipaksa ½, tiap kolom tinggal 40 mm — garis tanda tangan jadi pendek
  // dan namanya terpotong (terlihat pada uji cetak Apo 21 Jul).
  { id: 'sk', label: 'Syarat & Ketentuan', wajib: true },
  { id: 'ttd', label: 'Tanda tangan', wajib: true },
  // TIDAK ada blok "Ikuti kami" di sini (keputusan Apo 22 Jul): ajakan itu cukup di nota.
  // Terukur pula: satu baris tambahan membuat tanda terima 131,5 mm — lewat 129 mm yang
  // tersedia di ½ A4, jadi dua lembar. Tautan lacak (yang dikirim lewat WA) tetap ada di
  // kotak "Lacak status servis Anda".
  { id: 'footer', label: 'Tagline penutup' },
];

/** Blok TEKS buatan Apo — dirender sebagai paragraf biasa, isinya disaring seperti teks lain. */
const blokTambahanHTML = (id: string, tata: TataSimpan): string => {
  if (!id.startsWith(PREFIKS_TAMBAHAN)) return '';
  const t = isiTambahan(id, tata);
  return t.trim() ? `<div style="font-size:11.5px;line-height:1.5;margin:6px 0">${teksKaya(t)}</div>` : '';
};

/** Isi satu blok. Dipisah dari penyusunnya supaya urutan/tampil-sembunyi bisa diatur Apo. */
function tandaSec(id: string, d: TandaData, gar: boolean, tks: (id: string) => string, tata: TataSimpan): string {
  const kontak = [d.outletAlamat, d.outletWa].filter(Boolean).map(esc).join(' · ');
  const kel = d.kelengkapan.length ? d.kelengkapan.map(esc).join(', ') : '—';
  const track = d.token ? trackBase() + esc(d.token) : '';
  switch (id) {
    case 'kop':
      return `<div class="h"><div>${logoKop(26)}<div style="font-size:11px;color:#444">${esc(d.outletNama)}</div></div>
    <div class="c">${kontak}<br>Tgl terima: ${esc(d.tgl)}</div></div>`;
    case 'garansiTag':
      return gar ? `<div style="text-align:center;margin:2px 0 6px"><span style="display:inline-block;background:#fdecea;border:2px solid #c0392b;color:#c0392b;font-weight:bold;letter-spacing:2px;padding:3px 12px;border-radius:6px">${esc(tks('garansiTag'))}</span></div>` : '';
    case 'judul':
      return `<div style="text-align:center;font-weight:bold;letter-spacing:1px;color:#0A2A4A;margin:2px 0 8px">${esc(tks('judul'))}${gar ? ' (GARANSI)' : ''}</div>`;
    case 'nomor':
      return `<div class="no">No. Servis: ${esc(d.no)}</div>`;
    case 'data':
      return `<table>
    <tr><td class="k">Pelanggan</td><td>${esc(d.custNama)}</td></tr>
    <tr><td class="k">Unit</td><td>${esc(d.unit)}${d.sn ? ` (S/N ${esc(d.sn)})` : ''}</td></tr>
    ${d.warna ? `<tr><td class="k">Warna</td><td>${esc(d.warna)}</td></tr>` : ''}
    <tr><td class="k">Kelengkapan</td><td>${kel}</td></tr>
    ${d.sandi ? `<tr><td class="k">Pola/Sandi</td><td>${esc(d.sandi)}</td></tr>` : ''}
    ${d.kondisi ? `<tr><td class="k">Keterangan</td><td>${esc(d.kondisi)}</td></tr>` : ''}
    <tr><td class="k">Keluhan</td><td>${esc(d.keluhan)}</td></tr>
    <tr><td class="k">Estimasi</td><td>${rp(d.estBiaya)}${d.estSelesai ? ` · Est. selesai ${esc(d.estSelesai)}` : ''}${d.dp > 0 ? ` · DP ${rp(d.dp)} · Sisa ${rp(d.sisa)}` : ''}</td></tr>
    <tr><td class="k">Penerima</td><td>${esc(d.penerima)}</td></tr>
  </table>`;
    case 'tracking':
      return track ? `<div class="box" style="display:flex;gap:10px;align-items:center;font-size:11px"><div style="width:96px;height:96px;flex:none">${qrSVG(track, 3)}</div><div>🔎 <b>Lacak status servis Anda</b><br>Scan QR atau buka:<br><b>${track}</b></div></div>` : '';
    case 'sk':
      return `<div class="ttl">Syarat & Ketentuan</div>
  <ol class="sk">${skTanda().map((x) => `<li>${esc(x)}</li>`).join('')}</ol>`;
    case 'skKhusus':
      return d.caseKhusus ? `<div class="ttl" style="color:#c0392b">⚠️ S&K KHUSUS — Unit Tidak Dapat Diuji Fungsi Penuh${d.caseKhususKet ? ` (${esc(d.caseKhususKet)})` : ''}</div>
  <ol class="sk" style="color:#7a2018">${skKhusus().map((x) => `<li>${esc(x)}</li>`).join('')}</ol>` : '';
    case 'ttd':
      return `<div class="ttd"><div>Pelanggan<div class="sig">${esc(d.custNama)}</div></div><div>Penerima (CS)<div class="sig">${esc(d.penerima)}</div></div></div>`;
    case 'footer':
      return `<div style="text-align:center;color:#6b7a8a;font-size:11px;margin-top:14px;border-top:1px solid #dbe4ec;padding-top:8px"><b>${esc(profil().tagline)}</b></div>`;
    default:
      return blokTambahanHTML(id, tata);
  }
}

export function tandaTerimaHTML(d: TandaData, opts?: { garansi?: boolean; tata?: TataSimpan }): string {
  const gar = !!opts?.garansi;
  const tata = opts?.tata ?? docTata('tanda');
  const secs = terapkanTata(semuaBlok(TANDA_SECTIONS, tata), tata);
  return `<!doctype html><html lang="id"><head><meta charset="utf-8"><title>Tanda Terima ${esc(d.no)}</title>
<style>${DOC_CSS}</style></head><body><div class="doc">
  ${secBody(secs, (id) => tandaSec(id, d, gar, (x) => teksBlok(semuaBlok(TANDA_SECTIONS, tata).find((y) => y.id === x)!, tata), tata), tata.gaya)}
</div></body></html>`;
}

/** Surat Kuasa / Pernyataan Pengambilan Diwakilkan (pelepasan tuntutan). */
export const KUASA_SECTIONS: SecDef[] = [
  { id: 'kop', label: 'Kop toko', wajib: true, isiDari: 'Nama toko & cabang dari Profil Toko.' },
  { id: 'judul', label: 'Judul surat', wajib: true, teks: 'SURAT PERNYATAAN PENGAMBILAN & PELEPASAN TUNTUTAN' },
  // Kertas ½ A4 melebar: identitas di kiri, foto bukti di kanan. Kalau ditumpuk, surat ini
  // hampir pasti jadi dua lembar (terukur 192 mm vs ruang cetak 129 mm).
  { id: 'data', label: 'Identitas pemilik & pengambil', wajib: true, width: 'half' },
  { id: 'foto', label: 'Foto bukti pengambil', width: 'half' },
  { id: 'pernyataan', label: 'Pernyataan pelepasan tuntutan', wajib: true, teks: DIWAK_PERNYATAAN },
  { id: 'sk', label: 'Syarat & Ketentuan' },
  { id: 'tempat', label: 'Tempat & tanggal', wajib: true },   // surat pelepasan tuntutan tanpa tanggal = tak terbukti kapan berlaku
  { id: 'ttd', label: 'Tanda tangan (pengambil · CS · saksi)', wajib: true },
];

function kuasaSec(id: string, m: DocMeta, p: Pengambil, csNama: string, fotoUrl: string | null | undefined, ringkas: boolean, tks: (id: string) => string, tata: TataSimpan, ttdUrl?: string | null): string {
  const row = (k: string, v: string): string => `<tr><td class="k">${k}</td><td>: ${v}</td></tr>`;
  switch (id) {
    case 'kop':
      return `<div style="text-align:center;margin-bottom:6px"><div style="display:flex;justify-content:center">${logoKop(24)}</div><div style="font-size:10px;color:#666">${esc(m.outletNama)}</div></div>`;
    case 'judul':
      return `<h2>${esc(tks('judul'))}<br><span style="font-size:11px;font-weight:normal">Pengambilan Unit Diwakilkan / Tanpa Tiket — ${esc(m.outletNama)}</span></h2>`;
    case 'data':
      return `<table>
    ${row('No. Tanda Terima', esc(m.no))}
    ${row('Unit / Servis', esc(`${m.unit} · ${m.keluhan} · SN ${m.sn || '-'}`))}
    ${row('Pemilik — Nama', esc(m.custNama))}
    ${row('Pemilik — No. Telp', ringkas ? maskPhone(m.custWa) : esc(m.custWa || '-'))}
    ${row('Pengambil — Nama (KTP)', esc(p.nama || '-'))}
    ${ringkas ? row('Pengambil — No. KTP', '<i>tercatat di arsip toko</i>') : row('Pengambil — No. KTP', esc(p.ktp || '-'))}
    ${ringkas ? '' : row('Pengambil — Alamat (KTP)', esc(p.alamat || '-'))}
    ${row('Pengambil — No. HP', ringkas ? maskPhone(p.hp) : esc(p.hp || '-'))}
    ${row('Hubungan dgn Pemilik', esc(p.hubungan || '-'))}
  </table>`;
    case 'pernyataan':
      // Teks pernyataan boleh diubah Apo di editor; {pengambil} diganti nama sungguhan.
      // replaceAll: Apo boleh menyebut nama pengambil lebih dari sekali di kalimat hukumnya.
      return `<div class="pn">${teksKaya(tks('pernyataan')).replaceAll('{pengambil}', `<b>${esc(p.nama || '(pengambil)')}</b>`)}</div>`;
    case 'foto':
      return fotoUrl
        ? `<div style="text-align:center"><div style="font-size:10px;color:#666;margin-bottom:3px">Foto bukti — pengambil memegang unit</div><img src="${esc(fotoUrl)}" alt="foto pengambil" style="max-width:230px;max-height:230px;border:1px solid #ddd;border-radius:6px"></div>`
        : '';
    case 'sk': {
      const list = skKuasa();
      return list.length ? `<div class="ttl">Syarat & Ketentuan</div>
  <ol class="sk">${list.map((x) => `<li>${esc(x)}</li>`).join('')}</ol>` : '';
    }
    case 'tempat':
      return `<div style="font-size:11px;margin-top:6px">${esc(m.kota || 'Pangkalpinang')} / ${esc(m.tglAmbil || m.tgl)}</div>`;
    case 'ttd': {
      // Tanda tangan layar ditempel DI ATAS garis, jadi surat tetap sah walau tak dicetak.
      // Tanpa itu kolomnya kosong seperti biasa (untuk ditandatangani basah setelah dicetak).
      const coret = ttdUrl
        ? `<img src="${esc(ttdUrl)}" alt="tanda tangan pengambil" style="height:30px;display:block;margin:0 auto -4px">`
        : '';
      return `<div class="ttd"><div>Pengambil${coret}<div class="sig">${esc(p.nama || '')}</div></div><div>CS yang Melayani<div class="sig">${esc(csNama)}</div></div><div>Saksi<div class="sig"></div></div></div>`;
    }
    default:
      return blokTambahanHTML(id, tata);
  }
}

/**
 * `ringkas` = salinan untuk PELANGGAN (dikirim lewat tautan WA). Nomor KTP & alamat pengambil
 * DISEMBUNYIKAN: tautan bisa diteruskan ke siapa saja dan hidup berbulan-bulan, sedangkan NIK
 * adalah data identitas paling sensitif. Lembar cetak & arsip toko tetap lengkap (bukti hukum).
 */
export function suratKuasaHTML(m: DocMeta, p: Pengambil, csNama: string, fotoUrl?: string | null, ringkas = false, opts?: { tata?: TataSimpan; ttdUrl?: string | null }): string {
  const tata = opts?.tata ?? docTata('kuasa');
  const secs = terapkanTata(semuaBlok(KUASA_SECTIONS, tata), tata);
  return `<!doctype html><html lang="id"><head><meta charset="utf-8"><title>Surat Kuasa ${esc(m.no)}</title>
<style>${DOC_CSS}
  h2{text-align:center;color:#0A2A4A;font-size:15px;margin:4px 0 10px}
  .pn{font-size:11px;text-align:justify;line-height:1.5;margin:8px 0;padding:8px;border:1px solid #ddd;border-radius:6px;background:#fafbfc}
  /* Tiga tanda tangan BASAH (pengambil/CS/saksi) butuh ruang tulis lebih lega daripada dua
     tanda tangan di tanda terima — jangan ikut dirapatkan demi memuat ½ A4. */
  .sig{margin-top:38px}</style></head><body><div class="doc">
  ${secBody(secs, (id) => kuasaSec(id, m, p, csNama, fotoUrl, ringkas, (x) => teksBlok(semuaBlok(KUASA_SECTIONS, tata).find((y) => y.id === x)!, tata), tata, opts?.ttdUrl), tata.gaya)}
</div></body></html>`;
}

export const TRACK_SECTIONS: SecDef[] = [
  { id: 'judul', label: 'Judul kartu', wajib: true, teks: 'STATUS BARANG SERVIS ANDA' },
  { id: 'data', label: 'Data servis (no, unit, kerusakan)', wajib: true },
  { id: 'status', label: 'Status terkini', wajib: true },
  { id: 'qr', label: 'QR lacak status' },
  { id: 'sk', label: 'Syarat & Ketentuan' },
];

function trackSec(id: string, m: DocMeta, tks: (id: string) => string, tata: TataSimpan): string {
  const rw = (k: string, v: string): string => `<div class="rw"><span class="k">${k}</span><span>${v}</span></div>`;
  switch (id) {
    case 'judul':
      return `<div class="hd">${esc(tks('judul'))}</div>`;
    case 'data':
      return `${rw('No. Servis', `<b>${esc(m.no)}</b>`)}
  ${rw('Tgl Masuk', esc(m.tgl))}
  ${rw('Pemilik', `${esc(m.custNama)} - ${maskPhone(m.custWa)}`)}
  ${rw('Barang Servis', esc(`${m.jenis} ${m.merk} ${m.model}`.trim()))}
  ${rw('S/N', esc(m.sn || '-'))}
  ${rw('Kelengkapan', esc(m.kelengkapan.length ? m.kelengkapan.join(', ') : '-'))}
  ${rw('Kerusakan', esc(m.keluhan))}`;
    case 'status':
      return `<div class="st">Status: ${m.hold ? `${esc(m.status)} [Menunggu Sparepart]` : esc(m.status)}</div>`;
    case 'qr':
      return `<div style="text-align:center"><div style="margin:12px auto;width:130px">${qrSVG(trackBase() + esc(m.token || m.no), 5)}</div><div style="font-size:11px;color:#777;padding-bottom:6px">SERVICE ID</div></div>`;
    case 'sk': {
      const list = skTracking();
      return list.length
        ? `<div class="sk"><b>Syarat & Ketentuan ${esc(m.outletNama)}</b><ol style="margin:4px 0 0;padding-left:16px">${list.map((x) => `<li>${esc(x)}</li>`).join('')}</ol></div>`
        : '';
    }
    default:
      return blokTambahanHTML(id, tata);
  }
}

/**
 * Kartu Tracking — DIKIRIM KE PELANGGAN LEWAT WA, bukan dokumen cetak (keputusan Apo 21 Jul).
 * Karena itu lebarnya dijaga seukuran kartu (420px) agar enak dibaca di HP, dan tak perlu
 * dipaksa mengikuti ukuran kertas seperti dokumen cetak.
 */
export function trackingHTML(m: DocMeta, opts?: { tata?: TataSimpan }): string {
  const tata = opts?.tata ?? docTata('tracking');
  const secs = terapkanTata(semuaBlok(TRACK_SECTIONS, tata), tata);
  return `<!doctype html><html lang="id"><head><meta charset="utf-8"><title>Tracking ${esc(m.no)}</title>
<style>*{font-family:Arial,sans-serif;box-sizing:border-box}body{margin:0;background:#f0f4f8;padding:16px}
  .card{max-width:420px;margin:0 auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 8px 30px rgba(0,0,0,.12)}
  .hd{background:linear-gradient(90deg,#0A2A4A,#1FA9E0);color:#fff;padding:14px;text-align:center;font-weight:bold}
  .rw{display:flex;justify-content:space-between;padding:9px 14px;border-bottom:1px solid #eef2f6;font-size:13px;gap:12px}.rw .k{color:#777}
  .st{background:#fde9ea;color:#0A2A4A;padding:10px 14px;font-weight:bold;text-align:center}
  .sk{font-size:11px;color:#666;padding:12px 14px;border-top:1px solid #eef2f6}
  svg{max-width:100%;height:auto;display:block}
  @media print{body{background:#fff;padding:0}}</style></head><body><div class="card">
  ${secBody(secs, (id) => trackSec(id, m, (x) => teksBlok(semuaBlok(TRACK_SECTIONS, tata).find((y) => y.id === x)!, tata), tata), tata.gaya)}
</div></body></html>`;
}

/** Label stiker servis — QR + No. Servis + pelanggan. */
export function labelHTML(m: DocMeta): string {
  const track = trackBase() + esc(m.token || m.no);
  return `<!doctype html><html lang="id"><head><meta charset="utf-8"><title>Label ${esc(m.no)}</title>
<style>*{font-family:Arial,sans-serif}body{padding:10px;text-align:center}
  .lab{display:inline-block;border:1px solid #333;border-radius:8px;padding:10px 14px;width:230px}.b{font-weight:bold;color:#0A2A4A}
  svg{max-width:100%;height:auto;display:block}
  @media print{body{padding:0}}</style></head><body><div class="lab">
  <div class="b">${esc(m.outletNama)} · Label Servis</div>
  <div style="margin:8px auto;width:130px">${qrSVG(track, 5)}</div>
  <div>${esc(m.custNama)}</div><div class="b">${esc(m.no)}</div>
  <div style="font-size:10px;color:#666">Scan untuk cek status servis</div>
</div></body></html>`;
}
